import type { Filter, FilterNode } from "@estelle/domain";
import { useState } from "react";

import { matchesFilter } from "@/lib/filter-predicate";

/**
 * Write actions run on at most this many rows at once (#634). "Select all 78
 * that match" stops here and says so: a bulk write to Shopify of a thousand
 * orders is a job, not a click.
 */
export const SELECT_ALL_CAP = 200;

/**
 * A list's selection, kept across pages, sorting, page size and changes to
 * the filter or search (#634): pick five orders from five different filters,
 * then act once. It holds the rows themselves, not only their ids, so an
 * action can run on a row that is no longer on screen.
 *
 * It lives in the list screen's own state, so it is the list's: a different
 * section is a different screen and starts empty, and a reload clears it.
 */
export interface Selection<T> {
  /** The chosen rows, by id, in the order they were chosen. */
  rows: ReadonlyMap<string, T>;
  size: number;
  has: (id: string) => boolean;
  /** Tick or untick one row. */
  set: (row: T, on: boolean) => void;
  /** Tick or untick several — the header checkbox, "Select all that match". */
  setMany: (rows: readonly T[], on: boolean) => void;
  /** Keep only these: what is left after a run, the ones that failed. */
  keepOnly: (ids: ReadonlySet<string>) => void;
  clear: () => void;
  /** "View selected": the list shows the chosen rows instead of its page. */
  viewing: boolean;
  setViewing: (on: boolean) => void;
}

export function useSelection<T>(idOf: (row: T) => string): Selection<T> {
  const [rows, setRows] = useState<ReadonlyMap<string, T>>(new Map());
  const [viewingState, setViewing] = useState(false);
  // Nothing to view once nothing is chosen.
  const viewing = viewingState && rows.size > 0;

  return {
    rows,
    size: rows.size,
    has: (id) => rows.has(id),
    set: (row, on) => {
      setRows((current) => {
        const next = new Map(current);
        if (on) next.set(idOf(row), row);
        else next.delete(idOf(row));
        return next;
      });
    },
    setMany: (list, on) => {
      setRows((current) => {
        const next = new Map(current);
        for (const row of list) {
          if (on) next.set(idOf(row), row);
          else next.delete(idOf(row));
        }
        return next;
      });
    },
    keepOnly: (ids) => {
      setRows((current) => new Map([...current].filter(([id]) => ids.has(id))));
    },
    clear: () => {
      setRows(new Map());
      setViewing(false);
    },
    viewing,
    setViewing,
  };
}

/** Every field a filter names, however deep. */
function fieldsOf(node: Filter | FilterNode): string[] {
  return node.kind === "condition"
    ? [node.field]
    : node.children.flatMap((child) => fieldsOf(child));
}

/**
 * Whether a selected row would show under the filter in force, from the row
 * alone (#634). `undefined` when the filter names a field the row's projection
 * does not carry — a join only the server can answer — so the bar does not
 * claim a row is hidden when it cannot tell.
 */
export function inViewOf<T>(
  project: (row: T) => Record<string, unknown>,
  filter: Filter,
  now: number,
): (row: T) => boolean | undefined {
  const names = fieldsOf(filter);
  return (row) => {
    const projection = project(row);
    if (names.some((name) => !(name in projection))) return undefined;
    return matchesFilter(projection, filter, now);
  };
}

/** The bar's facts about a selection against the page on screen (#634). */
export function selectionFacts<T>({
  selection,
  pageRows,
  idOf,
  selectable = () => true,
  inView,
}: {
  selection: Pick<Selection<T>, "rows" | "has">;
  pageRows: readonly T[];
  idOf: (row: T) => string;
  selectable?: (row: T) => boolean;
  inView?: (row: T) => boolean | undefined;
}): { onPage: number; notInView: number; pageFullySelected: boolean } {
  const pageIds = new Set(pageRows.map(idOf));
  const onPage = [...selection.rows.keys()].filter((id) =>
    pageIds.has(id),
  ).length;
  const notInView = inView
    ? [...selection.rows.values()].filter((row) => inView(row) === false).length
    : 0;
  const eligible = pageRows.filter(selectable);
  const pageFullySelected =
    eligible.length > 0 && eligible.every((row) => selection.has(idOf(row)));
  return { onPage, notInView, pageFullySelected };
}

/**
 * "5 selected · 2 on this page, 3 on other pages", and, when the filter now
 * hides some of them, "3 not in this view" (#634).
 */
export function selectionSummary({
  selected,
  onPage,
  notInView,
}: {
  selected: number;
  onPage: number;
  notInView: number;
}): string {
  const elsewhere = selected - onPage;
  const parts = [`${String(selected)} selected`];
  if (elsewhere > 0) {
    parts.push(
      `${String(onPage)} on this page, ${String(elsewhere)} on other pages`,
    );
  }
  const head = parts.join(" · ");
  return notInView > 0
    ? `${head} · ${String(notInView)} not in this view`
    : head;
}

/**
 * The row the arrow keys step to from the open one (#644): the next or the
 * previous on the page, staying put at either end.
 */
export function stepRow(
  ids: readonly string[],
  current: string,
  delta: 1 | -1,
): string | null {
  const index = ids.indexOf(current);
  if (index === -1) return null;
  return ids[index + delta] ?? null;
}

/** Whether a key press belongs to a field someone is typing in. */
export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
  );
}

/** "Mark 40 orders confirmed?" names a few of them, never only a count. */
export function previewNames(names: readonly string[], shown = 4): string {
  if (names.length <= shown) return names.join(", ");
  return `${names.slice(0, shown).join(", ")} and ${String(names.length - shown)} more`;
}
